<template>
	<div class="common-tab">
		<div v-show="showLeft && !mobile" class="prev-btn" @click="scrollPrev">
			<svg-icons name="suf-right" />
		</div>
		<div class="tab-scroll" ref="tabScroll">
			<div class="tab-wrapper">
				<div
					v-for="(item, index) in option"
					:key="index"
					class="common-tab-item"
					:class="{ 'common-tab-item--active': item.value == innerValue }"
					@click="changeTab(item, index)"
					ref="tabRefs"
				>
					{{ item.label }}
				</div>
			</div>
		</div>
		<div v-show="showRight && !mobile" class="next-btn" @click="scrollNext">
			<svg-icons name="suf-right" />
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
	value: {
		// eslint-disable-next-line vue/require-prop-type-constructor
		type: String | Number,
		default: ''
	},
	option: {
		type: Array,
		default: () => [] // { label, value }
	},
	mobile: {
		type: Boolean,
		default: false
	}
});
const emits = defineEmits(['change', 'update:value']);
const innerValue = computed({
	get: () => props.value,
	set: val => {
		emits('update:value', val);
	}
});
const changeTab = (item, idx) => {
	if (innerValue.value == item.value) return;
	innerValue.value = item.value;
	emits('change', {
		index: idx,
		value: item.value,
		data: props.option[idx]
	});
};

const tabScroll = ref(null);
const tabRefs = ref([]);
const showLeft = ref(false);
const showRight = ref(false);
let currentTab = 0;
watch(
	() => props.option,
	() => {
		currentTab = 0;
		nextTick(() => {
			const { offsetWidth = 0, scrollWidth = 0 } = tabScroll.value;
			if (scrollWidth > offsetWidth) {
				showRight.value = true;
			}
		});
	},
	{
		immediate: true
	}
);
function scrollPrev() {
	showRight.value = true;
	nextTick(() => {
		// 父元素范围
		const { left: parentLeft } = tabScroll.value.getBoundingClientRect();
		const { offsetLeft: parentOffset, offsetWidth: parentWidth } = tabScroll.value;
		for (let i = currentTab; i >= 0; i--) {
			const { left: childLeft } = tabRefs.value[i].getBoundingClientRect();
			// 当前子元素超出显示范围
			if (childLeft < parentLeft && parentLeft - childLeft > 2) {
				currentTab = i;
				const { offsetLeft, offsetWidth } = tabRefs.value[i];
				const itemOffset = Math.max(0, offsetLeft + offsetWidth - parentOffset - parentWidth);
				tabScroll.value.scrollLeft = itemOffset;
				// 滚动到最左边
				if (itemOffset === 0) {
					showLeft.value = false;
				}
				return;
			}
		}
	});
}
function scrollNext() {
	showLeft.value = true;
	nextTick(() => {
		// 父元素范围
		const { right: parentRight } = tabScroll.value.getBoundingClientRect();
		const { offsetLeft: parentOffset } = tabScroll.value;
		for (let i = currentTab; i < tabRefs.value.length; i++) {
			const { right: childRight } = tabRefs.value[i].getBoundingClientRect();
			// 当前子元素超出显示范围
			if (parentRight < childRight && childRight - parentRight > 2) {
				currentTab = i;
				const { offsetLeft } = tabRefs.value[i];
				const itemOffset = offsetLeft - parentOffset;
				tabScroll.value.scrollLeft = itemOffset;
				// 滚动到最右边
				if (itemOffset + tabScroll.value.offsetWidth > tabScroll.value.scrollWidth) {
					showRight.value = false;
				}
				return;
			}
		}
	});
}
</script>

<style lang="less" scoped>
.common-tab {
	max-width: 100%;
	display: inline-flex;
	align-items: center;

	.tab-scroll {
		flex: 1;
		min-width: 0;
		overflow-x: auto;
		scroll-behavior: smooth;

		&::-webkit-scrollbar {
			display: none;
		}
	}
	.tab-wrapper {
		display: inline-flex;
		gap: 20px;
	}
	.next-btn {
		margin-left: 20px;
	}
	.prev-btn {
		margin-right: 20px;
		.svg-icon {
			transform: rotate(180deg);
		}
	}
	.prev-btn,
	.next-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 5px;
		border: 1px solid rgba(var(--surf-inverse-color-rgb), 0.4);
		border-radius: 4px;
		.svg-icon {
			width: 18px;
			height: 18px;
			color: var(--surf-inverse-color) !important;
		}
		cursor: pointer;
		&:hover {
			background: rgba(var(--surf-inverse-color-rgb), 0.2);
		}
	}

	&-item {
		flex-shrink: 0;
		padding: 5px 12px;
		font-size: 14px;
		line-height: 18px;
		color: var(--surf-inverse-color);
		border: 1px solid rgba(var(--surf-inverse-color-rgb), 0.4);
		background: transparent;
		cursor: pointer;
		border-radius: 4px;

		&:hover {
			background: rgba(var(--surf-inverse-color-rgb), 0.2);
			color: var(--surf-inverse-color);
		}
		&--active {
			background: var(--surf-inverse-color)!important;
			color: rgba(var(--surf-theme-color-rgb), 1)!important;
		}
	}
}
</style>
